import { useEffect } from 'react';
import { MotionConfig, motion } from 'framer-motion';
import { Award, BookOpen, HelpCircle, LayoutGrid, Settings } from 'lucide-react';
import { useStore, type Screen } from './store/useStore';
import { publicUrl } from './native/native';
import { HookScreen } from './screens/HookScreen';
import { DumpScreen } from './screens/DumpScreen';
import { SortingScreen } from './screens/SortingScreen';
import { MatrixScreen } from './screens/MatrixScreen';
import { MasteryScreen } from './screens/MasteryScreen';
import { PaywallSheet } from './components/PaywallSheet';
import { SettingsSheet } from './components/SettingsSheet';
import { RolloverModal } from './components/RolloverModal';
import { ScanPackModal } from './components/ScanPackModal';
import { AiConsentSheet } from './components/AiConsentSheet';
import { TrialBanner } from './components/TrialBanner';
import { SignedOutBar, SyncStatusChip } from './components/SyncStatusChip';
import { Logo } from './components/Logo';

const NAV: { screen: Screen; label: string; icon: typeof LayoutGrid }[] = [
  { screen: 'matrix', label: 'Workspace', icon: LayoutGrid },
  { screen: 'mastery', label: 'Mastery Log', icon: Award },
];

export default function App() {
  const screen = useStore((s) => s.screen);
  const openSettings = useStore((s) => s.openSettings);
  const setScreen = useStore((s) => s.setScreen);

  const theme = useStore((s) => s.theme);
  const textScale = useStore((s) => s.textScale);
  const calmMotion = useStore((s) => s.calmMotion);
  const easyFont = useStore((s) => s.easyFont);
  const tabbed = screen === 'matrix' || screen === 'mastery';

  // Apply light/dark: user preference wins; 'system' follows the OS live.
  // The native status bar follows the same result.
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && mql.matches);
      document.documentElement.classList.toggle('dark', dark);
      void import('./native/native').then((n) => n.syncNativeChrome(dark));
    };
    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [theme]);

  // Accessibility prefs → root element (rem-based sizes scale with html).
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize =
      textScale === 'compact' ? '87.5%' : textScale === 'relaxed' ? '112.5%' : '100%';
    root.classList.toggle('calm', calmMotion);
    root.classList.toggle('easy-font', easyFont);
  }, [textScale, calmMotion, easyFont]);

  // Boot the cloud-sync engine (no-op unless Firebase is configured),
  // dismiss the native splash, and honor launch shortcuts (?action=dump).
  useEffect(() => {
    void import('./sync/firebaseSync').then((m) => m.startSync());
    void import('./native/native').then((n) => n.hideSplash());
    const action = new URLSearchParams(window.location.search).get('action');
    if (action === 'dump' || action === 'scan') {
      useStore.getState().setImportStrategy('merge');
      useStore.getState().setScreen('dump');
    }
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <div className="flex min-h-screen w-full bg-slate-100 dark:bg-slate-950">
      {/* Desktop sidebar navigation */}
      {tabbed && (
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col gap-1 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-4 lg:flex">
          <div className="mb-4 flex items-center gap-2.5 px-2 pt-2">
            <Logo size={42} />
            <div>
              <h1 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white">ZoneIn AI</h1>
              <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Instant Priority Matrix</p>
            </div>
          </div>

          {NAV.map(({ screen: navScreen, label, icon: Icon }) => (
            <button
              key={navScreen}
              onClick={() => setScreen(navScreen)}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                screen === navScreen
                  ? 'bg-brand-600/15 text-brand-600 dark:text-brand-400'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}

          {/* The guide is a first-class destination, not a buried link. */}
          <a
            href={publicUrl('/guide.html')}
            target="_blank"
            rel="noopener"
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
          >
            <BookOpen size={18} />
            User Guide
          </a>

          <div className="mt-auto space-y-3 px-2 pb-2">
            <SyncStatusChip />
            <div className="flex items-center justify-between">
              <TrialBanner />
              <button
                aria-label="Settings"
                onClick={openSettings}
                className="rounded-lg p-2 text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
              >
                <Settings size={17} />
              </button>
            </div>
          </div>
        </aside>
      )}

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        {/* Mobile header */}
        {tabbed && (
          <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 dark:border-slate-800/80 bg-white/80 dark:bg-slate-900/80 px-4 py-3 backdrop-blur-md lg:hidden">
            <div className="flex items-center gap-2">
              <Logo size={34} />
              <div>
                <h1 className="text-sm font-extrabold tracking-tight text-slate-900 dark:text-white">ZoneIn AI</h1>
                <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Instant Priority Matrix</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <TrialBanner />
              <a
                href={publicUrl('/guide.html')}
                target="_blank"
                rel="noopener"
                aria-label="User guide"
                title="User guide"
                className="rounded-lg p-2 text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white active:scale-95"
              >
                <HelpCircle size={16} />
              </a>
              <button
                aria-label="Settings"
                onClick={openSettings}
                className="rounded-lg p-2 text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white active:scale-95"
              >
                <Settings size={16} />
              </button>
            </div>
          </header>
        )}
        {tabbed && <SignedOutBar />}

        <main className={`relative flex-1 ${tabbed ? 'pb-24 lg:pb-0' : ''}`}>
          {/* Enter-only fade. Deliberately NOT AnimatePresence mode="wait":
              interrupted exit transitions can wedge, leaving a half-faded,
              unclickable screen. Reliability beats a cross-fade. */}
          <motion.div
            key={screen}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="min-h-full"
          >
            {screen === 'hook' && <HookScreen />}
            {screen === 'dump' && <DumpScreen />}
            {screen === 'sorting' && <SortingScreen />}
            {screen === 'matrix' && <MatrixScreen />}
            {screen === 'mastery' && <MasteryScreen />}
          </motion.div>
        </main>

        {/* Mobile bottom tabs.
            Register #38 (Kirsty): tabs sat flush against the screen edge,
            inside Android's gesture-navigation strip — taps aimed at
            Workspace/Mastery triggered the system back/home gesture and
            exited the app. The nav now rides ABOVE the gesture zone:
            safe-area inset padding (10px minimum even where insets
            report 0) keeps every touch target out of the OS's territory. */}
        {tabbed && (
          <nav
            className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-2 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 lg:hidden"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 10px)' }}
          >
            {NAV.map(({ screen: navScreen, label, icon: Icon }) => (
              <button
                key={navScreen}
                onClick={() => setScreen(navScreen)}
                className={`flex h-14 flex-col items-center justify-center gap-1 transition-colors ${
                  screen === navScreen ? 'text-brand-600 dark:text-brand-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                <Icon size={19} />
                <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
              </button>
            ))}
          </nav>
        )}
      </div>

      <PaywallSheet />
      <SettingsSheet />
      <RolloverModal />
      <ScanPackModal />
      <AiConsentSheet />
    </div>
    </MotionConfig>
  );
}
